/**
 * Auth configuration for the staff web app.
 *
 * Production staff auth is Microsoft Entra ID (RBAC via Entra groups/roles, see
 * docs/contracts/api-contracts.md §Authentication). This module scaffolds that
 * wiring but the demo build uses a local credential check (see AuthProvider).
 * When moving to production, replace the demo login with an MsalProvider using
 * `msalConfig` / `loginRequest` below and drop `DEMO_USERS`.
 */
import type { Role } from '../api/types';

/** When true (default for the demo), use local demo credentials, not Entra ID. */
export const DEMO_MODE = import.meta.env.VITE_DEMO_MODE !== 'false';

/**
 * The single credential handed to the client for the deployed demo. Sourced
 * from the deploy environment (`VITE_DEMO_EMAIL` / `VITE_DEMO_PASSWORD`, set by
 * infra/bicep/mock and .env.example), defaulting to the documented values so a
 * fresh `docker compose up` works with no extra config. Mapped to `staff_admin`
 * so the handoff account can see every queue and page. See MOCK_DEPLOY.md.
 */
const HANDOFF_EMAIL = import.meta.env.VITE_DEMO_EMAIL ?? 'demo@enable.health';
const HANDOFF_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD ?? 'Enable2Demo!';
const HANDOFF_TENANT_ID = import.meta.env.VITE_TENANT_ID ?? 'hosp-001';

export interface DemoUser {
  username: string;
  password: string;
  userId: string;
  name: string;
  email: string;
  role: Role;
  tenantId: string;
}

/**
 * Seeded demo staff accounts. Each maps to a role in the FND-005 matrix so the
 * UI can demonstrate role-scoped queues end-to-end against mock data. The first
 * entry is the client handoff credential (admin, env-configurable); the rest
 * are role-specific logins for demonstrating role-scoped behavior.
 */
export const DEMO_USERS: DemoUser[] = [
  {
    username: HANDOFF_EMAIL,
    password: HANDOFF_PASSWORD,
    userId: 'staff-admin-001',
    name: 'Demo Admin',
    email: HANDOFF_EMAIL,
    role: 'staff_admin',
    tenantId: HANDOFF_TENANT_ID,
  },
  {
    username: 'pas',
    password: 'demo',
    userId: 'staff-pas-001',
    name: 'Pat Singh (PAS)',
    email: 'pas@demo.enable.health',
    role: 'pas',
    tenantId: 'hosp-001',
  },
  {
    username: 'bas',
    password: 'demo',
    userId: 'staff-bas-001',
    name: 'Bailey Adams (BAS)',
    email: 'bas@demo.enable.health',
    role: 'bas',
    tenantId: 'hosp-001',
  },
  {
    username: 'finance',
    password: 'demo',
    userId: 'staff-fin-001',
    name: 'Fran Reyes (Finance)',
    email: 'finance@demo.enable.health',
    role: 'finance',
    tenantId: 'hosp-001',
  },
];

/** MSAL config, scaffolded for the production Entra ID path. */
export const msalConfig = {
  auth: {
    clientId: 'set-in-prod',
    authority: 'https://login.microsoftonline.com/demo-tenant',
    redirectUri: 'http://localhost:5177',
  },
  cache: { cacheLocation: 'sessionStorage' as const },
};

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};
